import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const HOVER_DELAY = 650;
const HOLD_DELAY = 550;
const TOUCH_TOOLTIP_DURATION = 2400;

const ActionTooltipButton = forwardRef(function ActionTooltipButton(
  { label, action, tooltipEnabled, children, style, ...buttonProps }, forwardedRef,
) {
  const buttonRef = useRef(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const touchStart = useRef(null);
  const held = useRef(false);
  const [tooltip, setTooltip] = useState(null);

  const clearShowTimer = () => {
    clearTimeout(showTimer.current);
    showTimer.current = null;
  };
  const clearHideTimer = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewportWidth = window.innerWidth;
    const edge = Math.min(220, viewportWidth - 16) / 2 + 8;
    const left = Math.max(edge, Math.min(rect.left + rect.width / 2, viewportWidth - edge));
    const above = rect.top > 64;
    setTooltip({ left, top: above ? rect.top - 8 : rect.bottom + 8, above });
  };

  useEffect(() => () => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
  }, []);

  const onPointerEnter = event => {
    if (!tooltipEnabled || (event.pointerType !== 'mouse' && event.pointerType !== 'pen')) return;
    clearShowTimer();
    clearHideTimer();
    showTimer.current = setTimeout(showTooltip, HOVER_DELAY);
  };
  const onPointerLeave = event => {
    if (!tooltipEnabled || event.pointerType === 'touch') return;
    clearShowTimer();
    setTooltip(null);
  };
  const onPointerDown = event => {
    if (!tooltipEnabled || event.pointerType !== 'touch') return;
    clearShowTimer();
    clearHideTimer();
    setTooltip(null);
    held.current = false;
    touchStart.current = { x: event.clientX, y: event.clientY };
    showTimer.current = setTimeout(() => {
      held.current = true;
      showTooltip();
    }, HOLD_DELAY);
  };
  const onPointerMove = event => {
    if (!touchStart.current || event.pointerType !== 'touch') return;
    if (Math.hypot(event.clientX - touchStart.current.x, event.clientY - touchStart.current.y) > 10) {
      clearShowTimer();
      touchStart.current = null;
      if (held.current) setTooltip(null);
    }
  };
  const onPointerUp = event => {
    if (event.pointerType !== 'touch') return;
    clearShowTimer();
    touchStart.current = null;
    if (held.current) hideTimer.current = setTimeout(() => setTooltip(null), TOUCH_TOOLTIP_DURATION);
  };
  const onPointerCancel = () => {
    clearShowTimer();
    touchStart.current = null;
    held.current = false;
    setTooltip(null);
  };
  const onClick = event => {
    if (held.current) {
      event.preventDefault();
      event.stopPropagation();
      held.current = false;
      return;
    }
    clearHideTimer();
    setTooltip(null);
    action?.(event);
  };

  return <>
    <button
      {...buttonProps}
      ref={node => {
        buttonRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      type="button"
      title={tooltipEnabled ? undefined : label}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onContextMenu={event => {
        if (tooltipEnabled && (touchStart.current || held.current)) event.preventDefault();
      }}
      style={{ ...style, touchAction: tooltipEnabled ? 'manipulation' : undefined }}
    >{children}</button>
    {tooltip && createPortal(
      <div role="tooltip" style={{
        position: 'fixed', left: tooltip.left, top: tooltip.top,
        transform: tooltip.above ? 'translate(-50%, -100%)' : 'translateX(-50%)',
        maxWidth: 'min(220px, calc(100vw - 16px))', padding: '7px 10px',
        borderRadius: 7, border: '1px solid #64748b',
        background: 'rgba(15, 23, 42, 0.98)', color: '#f8fafc',
        fontSize: 12, fontWeight: 700, lineHeight: 1.3, textAlign: 'center',
        whiteSpace: 'normal', overflowWrap: 'break-word',
        boxShadow: '0 6px 18px rgba(0,0,0,0.5)', pointerEvents: 'none', zIndex: 500,
      }}>{label}</div>, document.body,
    )}
  </>;
});

export default ActionTooltipButton;
